"use client";

import { c as _c } from "react/compiler-runtime";
import { useDebounce } from "@dc/ui/hooks/useDebounce";
import { isRecord } from "@dc/utils";
import Cookies from "js-cookie";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useAbility } from "@/hooks/useAbility";
import { EVENTS, useAnalytics } from "@/hooks/useAnalytics";
import { useOrganization } from "@/hooks/useOrganization";
import { getAuthBackend } from "@/lib/authBackend/resolve";
import { type Organization, useOrganizationsInfinite } from "../useOrganizationsInfinite/useOrganizationsInfinite";
const SEARCH_DEBOUNCE_MS = 500;
const MIN_ORGS_FOR_SEARCH = 5;
export type { Organization };

// The API sends null when an org's entitlement lookup fails, despite the generated `object` type.
function isNotEntitled(entitlement: unknown): boolean {
  return isRecord(entitlement) && entitlement.status === "not_entitled";
}
export interface UseOrgSwitcherReturn {
  organizations: Organization[];
  currentOrganization: Organization | null;
  currentOrganizationId: string | null;
  totalCount: number;
  hasMore: boolean;
  isPending: boolean;
  isFetchingMore: boolean;
  showSearch: boolean;
  searchValue: string;
  setSearchValue: (value: string) => void;
  loadMore: () => void;
  selectOrganization: (org: Organization) => void;
  handleLogout: () => Promise<void>;
  isLoggingOut: boolean;
  /** Organization id -> disabled reason, derived from entitlement. Always empty for a System Admin. */
  disabledOrganizations: Record<string, string>;
}
export function useOrgSwitcher() {
  const $ = _c(45);
  const {
    currentOrganization,
    currentOrganizationId,
    setCurrentOrganization
  } = useOrganization();
  const {
    trackEvent
  } = useAnalytics();
  const t = useTranslations("navigation.org_switcher");
  const {
    can
  } = useAbility();
  let t0;
  if ($[0] !== can) {
    t0 = can("manage", {
      scopeType: "SYSTEM"
    });
    $[0] = can;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isSystemAdmin = t0;
  const [searchValue, setSearchValue] = useState("");
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [totalCountFromFirstLoad, setTotalCountFromFirstLoad] = useState(null);
  let t1;
  if ($[2] !== searchValue) {
    t1 = searchValue.trim();
    $[2] = searchValue;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const debouncedSearch = useDebounce(t1, SEARCH_DEBOUNCE_MS);
  let t2;
  let t3;
  if ($[4] !== debouncedSearch || $[5] !== trackEvent) {
    t2 = () => {
      if (debouncedSearch) {
        trackEvent(EVENTS.SEARCH_QUERY, {
          search_query: debouncedSearch,
          location: "organization switcher"
        });
      }
    };
    t3 = [debouncedSearch, trackEvent];
    $[4] = debouncedSearch;
    $[5] = trackEvent;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  const t4 = debouncedSearch || undefined;
  const t5 = isSystemAdmin ? undefined : "entitlement";
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = {
      search: t4,
      include: t5
    };
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const {
    data,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useOrganizationsInfinite(t6);
  let organizations;
  let t7;
  if ($[11] !== data?.pages || $[12] !== isSystemAdmin || $[13] !== t) {
    organizations = data?.pages.flatMap(_temp) ?? [];
    t7 = isSystemAdmin ? {} : organizations.reduce((acc, org) => {
      if (isNotEntitled(org.attributes?.entitlement)) {
        acc[org.id] = t("license_inactive");
      }
      return acc;
    }, {});
    $[11] = data?.pages;
    $[12] = isSystemAdmin;
    $[13] = t;
    $[14] = organizations;
    $[15] = t7;
  } else {
    organizations = $[14];
    t7 = $[15];
  }
  const disabledOrganizations = t7;
  const totalCount = data?.pages[0]?.meta?.total ?? 0;
  let t8;
  if ($[16] !== data || $[17] !== debouncedSearch || $[18] !== totalCountFromFirstLoad) {
    t8 = () => {
      if (totalCountFromFirstLoad === null && !debouncedSearch && data?.pages[0]?.meta?.total !== undefined) {
        setTotalCountFromFirstLoad(data.pages[0].meta.total);
      }
    };
    $[16] = data;
    $[17] = debouncedSearch;
    $[18] = totalCountFromFirstLoad;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = data?.pages;
  let t10;
  if ($[20] !== debouncedSearch || $[21] !== t9 || $[22] !== totalCountFromFirstLoad) {
    t10 = [t9, debouncedSearch, totalCountFromFirstLoad];
    $[20] = debouncedSearch;
    $[21] = t9;
    $[22] = totalCountFromFirstLoad;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  useEffect(t8, t10);
  const showSearch = (totalCountFromFirstLoad ?? 0) > MIN_ORGS_FOR_SEARCH;
  let t11;
  if ($[24] !== fetchNextPage || $[25] !== hasNextPage || $[26] !== isFetchingNextPage) {
    t11 = () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    $[24] = fetchNextPage;
    $[25] = hasNextPage;
    $[26] = isFetchingNextPage;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const loadMore = t11;
  let t12;
  if ($[28] !== setCurrentOrganization) {
    t12 = org_0 => {
      setCurrentOrganization(org_0);
    };
    $[28] = setCurrentOrganization;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const selectOrganization = t12;
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = async () => {
      setIsLoggingOut(true);
      ;
      try {
        await getAuthBackend().signOut({
          global: true
        });
      } catch (t14) {
        const error = t14;
        console.error("Logout error:", error);
      }
      Cookies.set("x-org-id", "", {
        path: "/",
        maxAge: 0
      });
      Cookies.set("x-team-id", "", {
        path: "/",
        maxAge: 0
      });
    };
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const handleLogout = t13;
  const t14 = !!hasNextPage;
  let t15;
  if ($[31] !== currentOrganization || $[32] !== currentOrganizationId || $[33] !== disabledOrganizations || $[34] !== isFetchingNextPage || $[35] !== isLoggingOut || $[36] !== isPending || $[37] !== loadMore || $[38] !== organizations || $[39] !== searchValue || $[40] !== selectOrganization || $[41] !== showSearch || $[42] !== t14 || $[43] !== totalCount) {
    t15 = {
      organizations,
      currentOrganization,
      currentOrganizationId,
      totalCount,
      hasMore: t14,
      isPending,
      isFetchingMore: isFetchingNextPage,
      showSearch,
      searchValue,
      setSearchValue,
      loadMore,
      selectOrganization,
      handleLogout,
      isLoggingOut,
      disabledOrganizations
    };
    $[31] = currentOrganization;
    $[32] = currentOrganizationId;
    $[33] = disabledOrganizations;
    $[34] = isFetchingNextPage;
    $[35] = isLoggingOut;
    $[36] = isPending;
    $[37] = loadMore;
    $[38] = organizations;
    $[39] = searchValue;
    $[40] = selectOrganization;
    $[41] = showSearch;
    $[42] = t14;
    $[43] = totalCount;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  return t15;
}
function _temp(page) {
  return page.data ?? [];
}